import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ToastViewport } from '@/components/Toast.js';
import { BrandMark, GlassButton } from '@/components/effects/index.js';
import { useAuthStore } from '@/stores/authStore.js';
import { TZ_LABEL, useDisplayStore } from '@/stores/displayStore.js';
import { useNavBadges } from '@/lib/useNavBadges.js';

/**
 * Layout — top-bar shell that mirrors web/marketing/Nav: sticky glass
 * header, brand on the left, route nav in the middle, account + tz +
 * logout on the right. The page chrome stays out of the way visually so
 * dashboards / pages own the screen real estate.
 *
 * Subtle "aurora" radial fade lives in the background — the same trick
 * web's Hero uses to add depth without being noisy.
 */

/**
 * Nav grouped semantically — 4 buckets shown left-to-right with a thin
 * vertical divider between groups. Single horizontal level (no dropdown)
 * because at this scale (12 items) a second click hurts more than helps.
 *
 * `/pricing` is intentionally NOT in nav: we settled on "promo codes,
 * never direct price edits" as the pricing-change strategy, so exposing
 * pricing here invites mistakes. The route still exists for direct URL
 * access if we ever need it again.
 */
const navGroups = [
  {
    items: [
      { to: '/dashboard', label: '总览' },
      { to: '/roadmap', label: '看板' },
      { to: '/users', label: '用户' },
      { to: '/orders', label: '订单' },
      { to: '/feedback', label: '反馈' },
    ],
  },
  {
    items: [
      { to: '/xianyu', label: '闲鱼' },
      { to: '/codes', label: '兑换码' },
      { to: '/promotion_codes', label: '折扣码' },
    ],
  },
  {
    items: [
      { to: '/visitors', label: '访客' },
      { to: '/devices', label: '设备' },
      { to: '/audit_logs', label: '日志' },
    ],
  },
  {
    items: [
      { to: '/releases', label: '热更新' },
      { to: '/settings', label: '设置' },
    ],
  },
] as const;

/**
 * Red "needs attention" badge for a nav tab. Two variants:
 *   corner — absolute, sits at the top-right of a desktop pill tab
 *   inline — flows after the label in the mobile menu rows
 * `showCount` renders the number (feedback); otherwise a plain dot (releases).
 */
function NavDot({
  count,
  showCount,
  variant,
  title,
}: {
  count: number;
  showCount: boolean;
  variant: 'corner' | 'inline';
  title: string;
}): JSX.Element | null {
  if (count <= 0) return null;
  const label = count > 99 ? '99+' : String(count);
  if (variant === 'inline') {
    return showCount ? (
      <span
        title={title}
        className="ml-2 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-[10px] font-semibold leading-none text-white"
      >
        {label}
      </span>
    ) : (
      <span title={title} className="ml-2 inline-block h-2 w-2 rounded-full bg-rose-500 align-middle" />
    );
  }
  return showCount ? (
    <span
      title={title}
      className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-[10px] font-semibold leading-none text-white flex items-center justify-center ring-2 ring-slate-950"
    >
      {label}
    </span>
  ) : (
    <span
      title={title}
      className="absolute top-0.5 right-1 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-slate-950"
    />
  );
}

export function Layout(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const admin = useAuthStore((s) => s.admin);
  const logout = useAuthStore((s) => s.logout);
  const tz = useDisplayStore((s) => s.tz);
  const toggleTz = useDisplayStore((s) => s.toggleTz);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const badges = useNavBadges(location.pathname);

  // Map a nav route → its red-dot badge, or null when nothing needs attention.
  function badgeFor(
    to: string,
  ): { count: number; showCount: boolean; title: string } | null {
    if (to === '/feedback' && badges.feedback > 0) {
      return { count: badges.feedback, showCount: true, title: `${badges.feedback} 条未处理反馈` };
    }
    if (to === '/orders' && badges.orders > 0) {
      return { count: badges.orders, showCount: true, title: `${badges.orders} 笔新订单（上次查看后）` };
    }
    if (to === '/users' && badges.users > 0) {
      return { count: badges.users, showCount: true, title: `${badges.users} 个新用户（上次查看后）` };
    }
    if (to === '/releases' && badges.releases > 0) {
      return { count: badges.releases, showCount: false, title: '有待审核的热更新草稿' };
    }
    return null;
  }

  // Close mobile menu when route changes — otherwise user taps a link and
  // ends up looking at the panel still open over their page.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  function handleLogout(): void {
    logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="page-surface relative min-h-screen text-slate-200">
      {/* 与 web 共用同一层连续受光表面 (page-surface ::before/::after) + 暗角,
          取代旧的 bg-radial-fade / bg-grid。admin 与 web 视觉统一。 */}
      <div className="page-vignette" aria-hidden />

      <header className="sticky top-0 z-30 border-b border-white/[0.06] bg-ink-950/80 backdrop-blur-xl backdrop-saturate-150">
        <div className="max-w-7xl mx-auto flex items-center gap-3 md:gap-6 px-4 md:px-6 h-14 md:h-16">
          <NavLink to="/dashboard" className="shrink-0">
            <BrandMark
              emojiClassName="text-xl"
              textClassName="font-display font-bold text-white tracking-tight text-base"
            />
          </NavLink>

          {/* Desktop nav — groups separated by a thin vertical divider */}
          <nav className="hidden md:flex items-center gap-0.5 ml-2">
            {navGroups.map((group, gi) => (
              <div key={gi} className="flex items-center gap-0.5">
                {gi > 0 && (
                  <span className="mx-2 h-4 w-px bg-white/10" aria-hidden />
                )}
                {group.items.map((link) => {
                  const badge = badgeFor(link.to);
                  return (
                    <NavLink
                      key={link.to}
                      to={link.to}
                      className={({ isActive }) =>
                        `relative px-3 py-1.5 rounded-full text-sm transition-colors ${
                          isActive
                            ? 'text-white bg-white/[0.08]'
                            : 'text-slate-400 hover:text-slate-100 hover:bg-white/[0.04]'
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {link.label}
                          {badge && <NavDot {...badge} variant="corner" />}
                          {isActive && (
                            <span className="absolute left-3 right-3 -bottom-px h-0.5 rounded-full bg-gradient-to-r from-shark-500 to-shark-300" />
                          )}
                        </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2 md:gap-3">
            <button
              type="button"
              onClick={toggleTz}
              className="hidden md:inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-slate-200 px-2.5 py-1 rounded-full border border-white/[0.08] hover:border-white/10 bg-white/[0.03] transition-colors"
              title="点击切换时区"
            >
              <span className="opacity-60">时区</span>
              <span>{TZ_LABEL[tz]}</span>
              <span className="opacity-50">⇄</span>
            </button>
            <span className="hidden sm:inline text-sm text-slate-400 max-w-[120px] truncate">
              {admin?.display_name ?? admin?.username}
            </span>
            <GlassButton size="sm" variant="ghost" onClick={handleLogout}>
              退出
            </GlassButton>
            {/* Mobile burger */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen((v) => !v)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-full bg-white/[0.06] hover:bg-white/[0.12] border border-white/[0.12] backdrop-blur-xl text-slate-200"
              aria-label="菜单"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {mobileMenuOpen ? (
                  <path d="M6 6l12 12M6 18L18 6" />
                ) : (
                  <>
                    <path d="M4 6h16" />
                    <path d="M4 12h16" />
                    <path d="M4 18h16" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu panel */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/[0.08] bg-slate-950/95 backdrop-blur-xl">
            <nav className="flex flex-col px-4 py-3 gap-0.5">
              {navGroups.flatMap((group, gi) => [
                gi > 0 ? (
                  <div key={`sep-${gi}`} className="my-1.5 h-px bg-white/[0.05]" aria-hidden />
                ) : null,
                ...group.items.map((link) => {
                  const badge = badgeFor(link.to);
                  return (
                    <NavLink
                      key={link.to}
                      to={link.to}
                      className={({ isActive }) =>
                        `flex items-center px-3 py-2.5 rounded-lg text-sm transition-colors ${
                          isActive
                            ? 'bg-white/[0.08] text-white'
                            : 'text-slate-300 hover:bg-white/[0.04] hover:text-white'
                        }`
                      }
                    >
                      {link.label}
                      {badge && <NavDot {...badge} variant="inline" />}
                    </NavLink>
                  );
                }),
              ])}
              <button
                type="button"
                onClick={toggleTz}
                className="mt-2 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:bg-white/[0.04] text-left flex items-center justify-between"
              >
                <span>时区</span>
                <span className="text-slate-300">{TZ_LABEL[tz]} ⇄</span>
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="mt-1 px-3 py-2.5 rounded-lg text-sm text-rose-300 hover:bg-rose-900/20 text-left"
              >
                退出登录（{admin?.display_name ?? admin?.username}）
              </button>
            </nav>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10">
        <Outlet />
      </main>
      <ToastViewport />
    </div>
  );
}
